/**
 * Mobile soft-keyboard chrome hiding.
 *
 * When an editable field takes focus (phone/OTP/request form fields), the
 * host page's pinned bars (`header.site`, `.service-nav`) are hidden so the
 * keyboard does not cover the field; when the keyboard closes they return
 * with a smooth animation. State is published as
 * `document.documentElement[data-keyboard="open"]` (host chrome) and as
 * `data-keyboard` on the widget host, so shadow CSS and page CSS share one
 * source of truth.
 *
 * Rules:
 * - focus (not blur) drives hiding: rapid focus changes between inputs never
 *   flicker because the close is debounced and cancelled by the next focus.
 * - a re-render that destroys the focused node is not a blur: the state stays
 *   open instead of flashing the pinned bars back mid-flow.
 * - reconciliation only ever opens: the absence of a focus signal never
 *   closes, so a missed event cannot leave the bars covering the field.
 * - visualViewport shrink confirms a real keyboard; focus alone is enough to
 *   hide immediately, the viewport only refines it.
 * - reduced-motion is honoured in CSS (transitions off), never in JS logic.
 * - restore runs on blur-to-nothing, viewport restore, view change and
 *   validation-error reveals.
 */

export const KEYBOARD_OPEN_ATTR = 'data-keyboard';
export const KEYBOARD_HEIGHT_RATIO = 0.85;

const EDITABLE_SELECTOR =
  'input:not([type="hidden"]):not([disabled]), textarea:not([disabled]), select:not([disabled]), [contenteditable="true"]';

export function isEditableField(node: unknown): boolean {
  if (!node || typeof (node as Element).matches !== 'function') return false;
  try {
    return (node as Element).matches(EDITABLE_SELECTOR);
  } catch {
    return false;
  }
}

/** True when the visual viewport shrank enough to mean a soft keyboard. */
export function viewportSuggestsKeyboard(baseHeight: number, currentHeight: number): boolean {
  if (!Number.isFinite(baseHeight) || !Number.isFinite(currentHeight)) return false;
  if (baseHeight <= 0 || currentHeight <= 0) return false;
  return currentHeight < baseHeight * KEYBOARD_HEIGHT_RATIO;
}

export interface KeyboardChromeHooks {
  onOpen?: () => void;
  onClose?: () => void;
  closeDelayMs?: number;
}

export class KeyboardChromeController {
  private focused: Element | null = null;
  private open = false;
  private closeTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly closeDelayMs: number;
  private readonly hooks: KeyboardChromeHooks;

  constructor(hooks: KeyboardChromeHooks = {}) {
    this.hooks = hooks;
    this.closeDelayMs = hooks.closeDelayMs ?? 150;
  }

  get isOpen(): boolean {
    return this.open;
  }

  /** The editable field focus is currently attributed to, if any. */
  get focusedElement(): Element | null {
    return this.focused;
  }

  handleFocusIn(target: unknown): boolean {
    if (!isEditableField(target)) return this.open;
    this.focused = target as Element;
    this.cancelClose();
    if (!this.open) {
      this.open = true;
      this.hooks.onOpen?.();
    }
    return this.open;
  }

  handleFocusOut(relatedTarget: unknown): boolean {
    if (isEditableField(relatedTarget)) {
      this.focused = relatedTarget as Element;
      this.cancelClose();
      return this.open;
    }
    this.focused = null;
    this.scheduleClose();
    return this.open;
  }

  /**
   * The focused node was destroyed (a re-render replaced the input while the
   * keyboard is open, e.g. the phone field going `readonly` during the OTP
   * request). Focus did not move to another control — there is simply nothing
   * focused right now — so scheduling a close would flash the pinned bars back
   * while the keyboard is still up. Stay open; the next focus, viewport
   * restore, validation reveal or navigation settles the state.
   */
  handleNodeRemoval(): boolean {
    this.focused = null;
    this.cancelClose();
    return this.open;
  }

  /**
   * Reconcile with the element that is actually focused right now.
   *
   * Only ever opens: a focus event that never arrived (or arrived before the
   * guards were listening) must not leave the pinned bars covering the field.
   * Closing stays with blur, viewport restore and navigation, which observe a
   * real loss of focus rather than the absence of a signal.
   */
  reconcileFocus(current: unknown): boolean {
    if (isEditableField(current)) return this.handleFocusIn(current);
    return this.open;
  }

  /** A viewport shrink while an editable field is focused opens; restore closes. */
  handleViewport(baseHeight: number, currentHeight: number): boolean {
    if (viewportSuggestsKeyboard(baseHeight, currentHeight)) {
      if (this.focused && !this.open) {
        this.open = true;
        this.hooks.onOpen?.();
      }
      this.cancelClose();
      return this.open;
    }
    if (this.open && !this.focused) {
      this.closeNow();
    }
    return this.open;
  }

  /** Navigation, view changes and validation reveals always restore. */
  forceClose(): void {
    this.focused = null;
    this.closeNow();
  }

  notifyValidationError(): void {
    // Validation keeps focus inside the form: cancel any pending close so the
    // chrome stays hidden while the customer fixes the field.
    if (this.focused) this.cancelClose();
  }

  private scheduleClose(): void {
    this.cancelClose();
    this.closeTimer = setTimeout(() => {
      this.closeTimer = null;
      // A rapid focus change re-sets `focused` before this fires.
      if (this.focused) return;
      this.closeNow();
    }, this.closeDelayMs);
  }

  private cancelClose(): void {
    if (this.closeTimer !== null) {
      clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }
  }

  private closeNow(): void {
    this.cancelClose();
    if (!this.open) return;
    this.open = false;
    this.hooks.onClose?.();
  }
}
